// src/components/attendance/AttendanceHeaderQuickAction.tsx
//
// CRM Attendance quick action for the SuiteShell header (ui-shell
// `HeaderQuickActions` slot). THIN ADAPTER over the shared presentation package
// `@roi-pros/ui-attendance`.
//
// This file owns ONLY transport + host wiring, all unchanged:
//   - the existing `platformAttendanceApi` RTK hooks (→ CRM BFF → Central),
//   - bounded 60s polling of current-session,
//   - the feature-gate mapping (404 → hidden; 401/403 → fail closed),
//   - the Central error-code → user-message mapping.
//
// ALL presentation — menu, elapsed timer, status chip, icons, derived state,
// action matrix, and accessibility — is owned by the shared component. Central
// remains the single source of truth: no local attendance state machine, no
// local persistence, no browser Central token, and no ids/timestamps sent.

import type { HeaderQuickActionProps } from '@levelup/ui-shell';
import { AttendanceHeaderQuickAction as SharedAttendanceHeaderQuickAction } from '@roi-pros/ui-attendance';
import { myWorkAvailabilityUrl, openMyWorkAvailability } from './centralAvailabilityLinks';
import { useActiveWorkspace } from '../../hooks/useActiveWorkspace';
import {
  useGetCurrentAttendanceSessionQuery,
  useAttendanceClockInMutation,
  useAttendanceClockOutMutation,
  useAttendanceStartBreakMutation,
  useAttendanceEndBreakMutation,
} from '../../api/platformAttendanceApi';

/** Bounded polling of current-session while mounted (no SSE). Transport concern. */
const POLL_MS = 60_000;

// Canonical Central error code → concise user message. Central owns the codes;
// this maps for display only and invents no transition semantics. Unknown codes
// return undefined so the shared component's neutral fallback is shown.
const ERROR_MESSAGES: Record<string, string> = {
  already_clocked_in: 'Already clocked in.',
  break_already_active: 'A break is already active.',
  invalid_attendance_transition: 'End the active break before clocking out.',
  no_active_break: 'No active break was found.',
  no_active_session: 'No active attendance session was found.',
  attendance_conflict: 'Attendance changed elsewhere. Status has been refreshed.',
};
function codeOf(err: unknown): string | undefined {
  const code = (err as { data?: { code?: unknown } })?.data?.code;
  return typeof code === 'string' ? code : undefined;
}
function resolveErrorMessage(err: unknown): string | undefined {
  const code = codeOf(err);
  return code ? ERROR_MESSAGES[code] : undefined;
}
function httpStatusOf(err: unknown): number | undefined {
  const s = (err as { status?: unknown })?.status;
  return typeof s === 'number' ? s : undefined;
}

/**
 * SuiteShell header quick action. Fetches current-session (polled), maps existing
 * transport values into the shared presentation, and forwards the existing
 * mutations as callbacks. Registration in SuiteShellAdapter is unchanged.
 */
/**
 * Rendered in TWO places, deliberately by the same component:
 *   - the SuiteShell `HeaderQuickActions` slot, which supplies its own props;
 *   - the CRM TopBar directly, which has none to give.
 *
 * The props were never read (the component is driven entirely by Central's
 * current-session response), so they are optional rather than duplicated into
 * a second wrapper that would drift.
 */
export function AttendanceHeaderQuickAction(_props: Partial<HeaderQuickActionProps> = {}) {
  const { data, isLoading, isError, error, refetch } = useGetCurrentAttendanceSessionQuery(undefined, {
    pollingInterval: POLL_MS,
  });
  const [clockIn] = useAttendanceClockInMutation();
  const [clockOut] = useAttendanceClockOutMutation();
  const [startBreak] = useAttendanceStartBreakMutation();
  const [endBreak] = useAttendanceEndBreakMutation();

  const session = data?.session ?? null;
  // Central scopes the member self-service surface per workspace; an unscoped
  // link reaches Central but renders an empty settings shell.
  const activeWorkspaceId = useActiveWorkspace()?.workspace?.id ?? null;
  const manageUrl = myWorkAvailabilityUrl(activeWorkspaceId);
  // Host maps HTTP → gate: 404 feature-gate → hidden; 401/403 → fail closed. The
  // shared component never inspects HTTP status itself.
  const status = httpStatusOf(error);
  const gated = status === 404 || status === 401 || status === 403;

  return (
    <SharedAttendanceHeaderQuickAction
      session={session}
      isLoading={isLoading}
      isError={isError}
      gated={gated}
      onClockIn={(workLocation) => clockIn({ workLocation }).unwrap().then(() => undefined)}
      onClockOut={() => clockOut().unwrap().then(() => undefined)}
      onStartBreak={(kind) => startBreak({ kind }).unwrap().then(() => undefined)}
      onEndBreak={() => endBreak().unwrap().then(() => undefined)}
      onRefresh={() => {
        void refetch();
      }}
      resolveErrorMessage={resolveErrorMessage}
      // MANAGEMENT ACTIONS — navigation only. Central owns the schedule and
      // leave editors; CRM renders none of its own, so these hand off to the
      // canonical surface. They are passed only when Central's web origin is
      // configured, so the overflow shows nothing rather than a dead link.
      {...(manageUrl
        ? {
            onManageSchedule: () => openMyWorkAvailability(activeWorkspaceId),
            onAddLeave: () => openMyWorkAvailability(activeWorkspaceId),
            onViewUpcomingLeave: () => openMyWorkAvailability(activeWorkspaceId),
            onViewAttendance: () => openMyWorkAvailability(activeWorkspaceId),
          }
        : {})}
    />
  );
}

export default AttendanceHeaderQuickAction;
